import {
  splitMarkdownAt,
} from "../lib/noteInlineMarkdown";
import { createEmptyBlock, isEmptyBlock } from "./noteBlocks";

export function getBlockPrimaryText(block) {
  if (!block) return "";
  if (block.type === "toggle") return block.title || "";
  if (block.type === "divider") return "";
  return block.text || "";
}

function blockWithText(block, text) {
  if (!block) return createEmptyBlock("paragraph");
  if (block.type === "toggle") return { ...block, title: text };
  if (block.type === "todo") return { ...block, text, checked: block.checked ?? false };
  if (block.type === "divider") return createEmptyBlock("paragraph");
  return { ...block, text };
}

function continuationBlock(block) {
  if (block.type === "bullet" || block.type === "numbered") {
    return createEmptyBlock(block.type);
  }
  if (block.type === "todo") {
    return { type: "todo", checked: false, text: "" };
  }
  return createEmptyBlock("paragraph");
}

/** Split one block into two at a plain-text cursor. */
export function splitBlockText(block, cursor, plainText) {
  const source = plainText ?? "";
  const { before, after } = splitMarkdownAt(source, cursor);

  return {
    current: blockWithText(block, before),
    next: after ? blockWithText(continuationBlock(block), after) : continuationBlock(block),
  };
}

/** Expand a block into multiple blocks from pasted plain text. */
export function expandPasteIntoBlocks(block, cursor, plainText, pastedText) {
  const source = plainText ?? "";
  const { before, after } = splitMarkdownAt(source, cursor);

  const normalized = (pastedText || "").replace(/\r\n/g, "\n");
  const lines = normalized.split("\n");
  if (lines.length <= 1) {
    const merged = before + (lines[0] || "") + after;
    return {
      blocks: [blockWithText(block, merged)],
      focusIndex: 0,
      cursor: before.length + (lines[0] || "").length,
    };
  }

  const blocks = [];
  blocks.push(blockWithText(block, before + lines[0]));

  for (let i = 1; i < lines.length - 1; i += 1) {
    blocks.push(blockWithText(createEmptyBlock("paragraph"), lines[i]));
  }

  blocks.push(blockWithText(createEmptyBlock("paragraph"), lines[lines.length - 1] + after));

  const focusIndex = blocks.length - 1;
  const focusCursor = lines[lines.length - 1].length;

  return { blocks, focusIndex, cursor: focusCursor };
}

export function readPlainTextFromElement(el, richText, htmlToMarkdown) {
  if (!el) return "";
  if (richText) return htmlToMarkdown(el.innerHTML ?? "");
  return el.textContent ?? "";
}

/** Merge current line into previous, or remove empty line and focus previous. */
export function mergeBlockWithPrevious(blocks, index, plainText, { atStart = false, field = "text" } = {}) {
  if (index <= 0 || !blocks[index]) {
    return null;
  }

  const current = blocks[index];
  const previous = blocks[index - 1];
  const text = plainText ?? (field === "body" ? current.body || "" : getBlockPrimaryText(current));

  if (current.type === "toggle" && field !== "body" && !text.trim() && (current.body || "").trim()) {
    return null;
  }

  const empty = !text.trim() && (field !== "body" || !(current.title || "").trim());

  if (previous.type === "divider") {
    if (!empty && !(field === "text" && atStart)) return null;
    const next = blocks.filter((_, i) => i !== index);
    return {
      blocks: next.length ? next : [{ type: "paragraph", text: "" }],
      focusIndex: index - 1,
      cursor: null,
    };
  }

  if (current.type === "divider") {
    const next = blocks.filter((_, i) => i !== index);
    return {
      blocks: next.length ? next : [{ type: "paragraph", text: "" }],
      focusIndex: Math.max(0, index - 1),
      cursor: null,
    };
  }

  if (current.type === "toggle" && field === "body" && empty) {
    const next = blocks.filter((_, i) => i !== index);
    const prevText = getBlockPrimaryText(previous);
    return {
      blocks: next.length ? next : [{ type: "paragraph", text: "" }],
      focusIndex: index - 1,
      cursor: prevText.length,
    };
  }

  if (empty) {
    const next = blocks.filter((_, i) => i !== index);
    const prevText = getBlockPrimaryText(previous);
    return {
      blocks: next.length ? next : [{ type: "paragraph", text: "" }],
      focusIndex: index - 1,
      cursor: prevText.length,
    };
  }

  if (!atStart) return null;

  const prevText = getBlockPrimaryText(previous);
  const merged = blockWithText(previous, prevText + text);
  const next = [...blocks];
  next[index - 1] = merged;
  next.splice(index, 1);

  return {
    blocks: next.length ? next : [{ type: "paragraph", text: "" }],
    focusIndex: index - 1,
    cursor: prevText.length,
  };
}

/** First block only: drop block formatting when empty. */
export function unformatFirstEmptyBlock(blocks, plainText) {
  const block = blocks[0];
  if (!block || block.type === "paragraph" || block.type === "divider") return null;
  if (!isEmptyBlock({ ...block, text: plainText, title: plainText })) return null;
  return {
    blocks: [{ type: "paragraph", text: "" }],
    focusIndex: 0,
    cursor: 0,
  };
}
